/**
 * posts.ts — client-side static blog post data.
 *
 * All content lives here as embedded markdown strings so no server/fs is needed.
 * Add new posts by appending to the POSTS array; the Blog and BlogPost pages
 * pick them up automatically.
 */

export interface Post {
    slug:        string
    title:       string
    date:        string          // ISO 8601
    tags:        string[]
    excerpt:     string
    readingTime: string          // e.g. "5 min read"
    content:     string          // markdown
}

export const POSTS: Post[] = [
    /* ─────────────────────────────────────────────────────── */
    /*  Community Impact: Empowering Girls in STEM             */
    /* ─────────────────────────────────────────────────────── */
    {
        slug:        'empowering-girls-in-stem',
        title:       'Empowering Girls in STEM — 1,000+ and Counting',
        date:        '2024-08-10',
        tags:        ['community', 'stem', 'outreach', 'education'],
        excerpt:     'How 20+ hands-on workshops with Arduino and Python helped break barriers and introduce over 1,000 girls to a future in technology.',
        readingTime: '5 min read',
        content: `
## The Problem Worth Solving

Globally, women represent fewer than 30 % of the STEM workforce. In many communities, the root cause isn't ability — it's access and early exposure. When I got involved in grassroots STEM outreach, I wanted to change that in a measurable way.

## What We Built Together

Over the course of several months we ran **20+ hands-on robotics and coding workshops** tailored for girls aged 11–18. Each session combined:

- **Arduino tinkering** — wiring circuits, writing basic C, seeing hardware come alive
- **Python fundamentals** — variables, loops, simple automation scripts
- **Project showcase** — every participant built something she could take home and explain to her family

The goal was never to produce professional developers overnight. It was to remove the phrase _"that's not for me"_ from the vocabulary of every girl who walked through the door.

## Impact by the Numbers

| Metric | Result |
|---|---|
| Girls introduced to STEM | **1,000+** |
| Workshops delivered | **20+** |
| Primary tools used | Arduino, Python |
| Focus area | Gender diversity in technology |

## What I Learned

Running these sessions taught me as much as it taught participants. Seeing someone write her first loop and immediately ask _"can I make it do this instead?"_ is one of the clearest signals that curiosity is already there — it just needs a trigger.

Technology is neutral. Who builds it is not. Every workshop was a small correction to that imbalance.

## What's Next

The curriculum is being documented so other organizers can run sessions independently. If you're building STEM programs for underrepresented groups and want to collaborate, [reach out](/contact).
        `.trim(),
    },

    /* ─────────────────────────────────────────────────────── */
    /*  Community Outreach & Digital Literacy                  */
    /* ─────────────────────────────────────────────────────── */
    {
        slug:        'community-outreach-digital-literacy',
        title:       'Community Outreach — Digital Literacy for 500+ Students',
        date:        '2024-06-20',
        tags:        ['community', 'education', 'outreach', 'mentorship'],
        excerpt:     'Mentoring beginners in programming, teaching digital literacy, and reaching 500+ community members to make technology more accessible.',
        readingTime: '4 min read',
        content: `
## Why Digital Literacy Matters

Knowing how to search the internet is not the same as understanding how it works. Understanding how it works is not the same as being able to build on top of it. There's a spectrum — and too many people in under-served communities never get past the first rung.

Community outreach work I've been part of has tried to address that directly.

## What the Program Covered

Across multiple sessions with schools, community centers, and local youth groups, we reached **500+ students and community members** with content that ranged from:

- **Digital literacy basics** — safe browsing, understanding files and folders, email etiquette
- **Intro to coding** — block-based programming progressing to text-based Python
- **Mentorship 1-on-1s** — pairing beginners with someone a few steps ahead of them

## Encouraging Technology for All

One of the consistent things I heard from participants was that they assumed programming was for "math people" or people who already owned expensive equipment. Both assumptions are wrong, and showing them that — with nothing more than a browser and a free account on a coding sandbox — was often the turning point.

## Mentorship Moments

Some of the most impactful moments weren't in the group sessions. They were the one-on-one conversations:

> _"I didn't know you could just… write code and it does something. I thought you needed special software."_

Normalizing the idea that anyone with a text editor and an internet connection can build something is still, somehow, a radical act in many communities.

## Impact Snapshot

| Metric | Result |
|---|---|
| Students & community members reached | **500+** |
| Core focus areas | Digital literacy, coding education |
| Format | Workshops + 1-on-1 mentorship |
| Goal | Lower the barrier to technology entry |

If you're running similar programs or want to partner, [let's connect](/contact).
        `.trim(),
    },

    /* ─────────────────────────────────────────────────────── */
    /*  Technical: pyworkflow-framework (Python)              */
    /* ─────────────────────────────────────────────────────── */
    {
        slug:        'pyworkflow-framework',
        title:       'pyworkflow-framework — CLI-First Workflow Automation in Python',
        date:        '2025-03-15',
        tags:        ['python', 'open-source', 'cli', 'automation', 'pypi'],
        excerpt:     'A developer-friendly Python workflow automation framework — think Airflow or Prefect, but simple enough to learn in five minutes, with zero infrastructure required.',
        readingTime: '8 min read',
        content: `
## PyWorkflow

A lightweight, developer-friendly Python workflow automation framework — think Airflow/Prefect/Dagster, but simple enough to learn in five minutes.

Define workflows as plain Python. Run them sequentially or in parallel. Get automatic retries, dependency resolution, scheduling, persistent history, a CLI, and a plugin system — with zero required infrastructure (no server, no database, no message broker).

\`\`\`python
from pyworkflow import Workflow, Task

workflow = Workflow("Data Processing")

workflow.add_task(Task("Download Data", download_function))
workflow.add_task(Task("Clean Data", clean_function, depends_on=["Download Data"]))
workflow.add_task(Task("Analyze Data", analyze_function, depends_on=["Clean Data"]))

report = workflow.run()
print(report.success, report.results)
\`\`\`

## Status

**v0.1.0** — first release. The core engine (workflows, tasks, dependencies, retries, parallel execution, scheduling, storage, CLI) is stable and fully tested.

## Installation

\`\`\`bash
pip install pyworkflow-framework
\`\`\`

Optional extras:
\`\`\`bash
pip install "pyworkflow-framework[viz]"   # graphviz/networkx-powered workflow.visualize()
pip install "pyworkflow-framework[dev]"   # pytest + pytest-cov for running the test suite
\`\`\`

From source:
\`\`\`bash
git clone https://github.com/Rapheal-Kwabena/pyworkflow.git
cd pyworkflow
pip install -e ".[dev]"
\`\`\`

## Quick Start

### 1. Define tasks as ordinary Python functions

\`\`\`python
def download_data():
    return {"rows": 1000}

def clean_data(context):
    # \`context\` holds the output of every completed task, keyed by task name
    raw = context["Download"]
    return {"rows": raw["rows"] - 10}  # drop 10 bad rows

def analyze_data(context):
    return f"Analyzed {context['Clean']['rows']} rows"
\`\`\`

A task function may optionally accept a \`context: dict\` parameter — PyWorkflow detects this via introspection and injects it automatically. Tasks that don't need upstream data can omit it entirely.

### 2. Build and run a workflow

\`\`\`python
from pyworkflow import Workflow, Task

workflow = Workflow("Data Processing")
workflow.add_task(Task("Download", download_data))
workflow.add_task(Task("Clean", clean_data, depends_on=["Download"]))
workflow.add_task(Task("Analyze", analyze_data, depends_on=["Clean"]))

report = workflow.run()

if report.success:
    print("All done:", report.results)
else:
    print("Failed tasks:", report.failed_tasks)
\`\`\`

### 3. Or build it fluently with .then(...)

\`\`\`python
workflow = Workflow("Data Processing")
(
    workflow
    .add_task(Task("Download", download_data))
    .then(Task("Clean", clean_data))
    .then(Task("Analyze", analyze_data))
)
workflow.run()
\`\`\`

## Advanced Examples

### Parallel execution

Independent tasks (no shared dependency chain) run concurrently with \`parallel=True\`:

\`\`\`python
workflow = Workflow("Fan-out", max_workers=4)
workflow.add_task(Task("Fetch US", fetch_us))
workflow.add_task(Task("Fetch EU", fetch_eu))
workflow.add_task(Task("Fetch APAC", fetch_apac))
workflow.add_task(Task("Merge", merge_regions, depends_on=["Fetch US", "Fetch EU", "Fetch APAC"]))

workflow.run(parallel=True)  # the three Fetch tasks run concurrently, then Merge
\`\`\`

### Retries and failure handling

\`\`\`python
workflow.add_task(
    Task(
        "Send Email",
        send_email,
        retries=3,
        retry_delay=5,          # seconds between attempts
        continue_on_failure=True,  # don't halt the whole workflow if this fails
        on_failure=lambda task, exc: alert_ops_team(task.name, exc),
    )
)

report = workflow.run()
if not report.success:
    workflow.retry_failed_tasks()  # re-run only what failed
\`\`\`

### Conditional / branching tasks

\`\`\`python
workflow.add_task(Task("Check Inventory", check_inventory))
workflow.add_task(
    Task(
        "Reorder Stock",
        reorder_stock,
        depends_on=["Check Inventory"],
        condition=lambda context: context["Check Inventory"]["low_stock"] is True,
    )
)
\`\`\`

### Scheduling

\`\`\`python
workflow.schedule("now")                          # run immediately
workflow.schedule("delay", delay=30)               # run once, in 30 seconds
workflow.schedule("interval", every=15)             # run every 15 minutes
workflow.schedule("cron", cron="0 8 * * 1-5")        # 8am on weekdays
workflow.schedule("daily", time="08:00")             # shorthand for daily cron
\`\`\`

### Persisting workflow history

\`\`\`python
from pyworkflow.storage import JSONStorage, SQLiteStorage

storage = JSONStorage()  # defaults to ~/.pyworkflow/
storage.save_workflow(workflow.to_dict())
storage.save_run(workflow.name, {"success": report.success, "results": report.results})

# Or, if you'd rather query with SQL:
sql_storage = SQLiteStorage()  # defaults to ~/.pyworkflow/pyworkflow.db
\`\`\`

## Plugins

Plugins turn common integrations into reusable Task factories:

\`\`\`python
from pyworkflow.plugins import EmailPlugin, APIPlugin

email = EmailPlugin()
email.setup(host="smtp.example.com", username="bot@example.com", password="...")

api = APIPlugin()
api.setup(base_url="https://api.example.com", headers={"Authorization": "Bearer ..."})

workflow.add_task(email.make_task("Notify", to="team@example.com", subject="Done", body="Workflow finished"))
workflow.add_task(api.make_task("Fetch Users", path="/users", method="GET"))
\`\`\`

## Command Line Interface

\`\`\`bash
pyworkflow create myworkflow      # scaffold myworkflow.py with a starter workflow
pyworkflow run myworkflow.py      # run it (add --parallel to run concurrently)
pyworkflow status "My Workflow"   # show last known state from local storage
pyworkflow history "My Workflow"  # show recent run history
pyworkflow logs "My Workflow"     # show failures/skips from the latest run
pyworkflow list                   # list all workflows in local storage
\`\`\`

---

Find the project on [PyPI](https://pypi.org/project/pyworkflow-framework/) and star the repository on [GitHub](https://github.com/Rapheal-Kwabena/pyworkflow)!
        `.trim(),
    },

    /* ─────────────────────────────────────────────────────── */
    /*  Technical: react-pyworkflow (React)                   */
    /* ─────────────────────────────────────────────────────── */
    {
        slug:        'react-pyworkflow',
        title:       'react-pyworkflow — Client-Side Workflow Orchestration in React',
        date:        '2025-03-22',
        tags:        ['open-source', 'npm', 'react', 'typescript', 'automation'],
        excerpt:     'Ported directly from PyWorkflow, this lightweight library brings reactive dependency resolution, async task pipelines, retries, and hooks to React.',
        readingTime: '6 min read',
        content: `
## react-pyworkflow ⚛️

A lightweight, client-side JavaScript/TypeScript workflow automation and orchestration library for React.

Ported directly from the Python version of \`pyworkflow\`, it lets you define async execution graphs (pipelines), handle complex dependencies, run tasks sequentially or concurrently, execute retries with delays, and track real-time status updates directly within React components.

## Installation

\`\`\`bash
npm install react-pyworkflow
# or
yarn add react-pyworkflow
# or
pnpm add react-pyworkflow
\`\`\`

## Features

- ⚛️ **React Hook Integration**: State changes trigger auto-re-renders of your React components dynamically during execution.
- ⚡ **Concurrent Execution**: Run independent branches in parallel via Promise.all with a single option switch.
- 🔄 **Automatic Retries**: Retries with configurable delays.
- 🛑 **Error and Callback Handling**: Control execution with \`continueOnFailure\` and \`onFailure\` hooks.
- 🔀 **Conditional Runs**: Skip tasks dynamically using context-based predicate checks.
- 🛠️ **Full TypeScript Support**: Out of the box ESM and CJS builds with \`.d.ts\` type declarations.

## Quick Start

### 1. Define Workflows and Tasks

Create a workflow graph exactly like PyWorkflow:

\`\`\`typescript
import { Workflow, Task } from "react-pyworkflow";

// 1. Create a workflow
export const dataPipeline = new Workflow("Data Pipeline");

// 2. Define tasks
const fetchTask = new Task({
  name: "Fetch Data",
  fn: async () => {
    const res = await fetch("https://api.example.com/data");
    return res.json();
  },
  retries: 2,
  retryDelay: 1000, // 1s
});

const filterTask = new Task({
  name: "Filter Data",
  dependsOn: ["Fetch Data"],
  fn: (context) => {
    // Upstream task results are automatically injected into context
    const data = context["Fetch Data"];
    return data.filter((item: any) => item.active);
  },
});

const saveTask = new Task({
  name: "Save Data",
  dependsOn: ["Filter Data"],
  fn: async (context) => {
    const filtered = context["Filter Data"];
    await fetch("https://api.example.com/save", {
      method: "POST",
      body: JSON.stringify(filtered),
    });
    return "Saved Successfully!";
  },
});

// 3. Compose workflow
dataPipeline.addTask(fetchTask).addTask(filterTask).addTask(saveTask);
\`\`\`

### 2. Connect to React Components

Use the \`useWorkflow\` hook to bind execution state reactively:

\`\`\`tsx
import React from "react";
import { useWorkflow } from "react-pyworkflow";
import { dataPipeline } from "./pipeline";

export default function PipelineMonitor() {
  const { state, taskStates, outputs, errors, isRunning, run, reset } = useWorkflow(dataPipeline);

  const handleStart = () => {
    run({ parallel: true }); // Runs independent tasks concurrently
  };

  return (
    <div style={{ padding: "20px", fontFamily: "sans-serif" }}>
      <h2>Workflow: {dataPipeline.name}</h2>
      <p>Status: <strong>{state}</strong></p>

      <div style={{ margin: "20px 0" }}>
        <button onClick={handleStart} disabled={isRunning}>
          Start Pipeline
        </button>
        <button onClick={reset} disabled={isRunning} style={{ marginLeft: "10px" }}>
          Reset
        </button>
      </div>

      <h3>Tasks:</h3>
      <ul>
        {Array.from(dataPipeline.tasks.keys()).map((name) => (
          <li key={name} style={{ margin: "10px 0" }}>
            <strong>{name}</strong>: <span style={{ color: getStatusColor(taskStates[name]) }}>{taskStates[name]}</span>
            {outputs[name] && <pre>{JSON.stringify(outputs[name], null, 2)}</pre>}
            {errors[name] && <p style={{ color: "red" }}>Error: {errors[name]}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function getStatusColor(status: string) {
  switch (status) {
    case "COMPLETED": return "green";
    case "RUNNING":
    case "RETRYING": return "orange";
    case "FAILED": return "red";
    case "SKIPPED": return "blue";
    default: return "gray";
  }
}
\`\`\`

## API Reference

### Workflow API

- \`constructor(name: string)\`
- \`addTask(task: Task): this\` — Add a task to the workflow.
- \`addTasks(tasks: Task[]): this\` — Add multiple tasks at once.
- \`then(task: Task): this\` — Add a task that depends on the previously added task.
- \`reset(): void\` — Reset the workflow and all tasks back to PENDING.
- \`validate(): void\` — Validate dependencies and detect cycles.
- \`run(options?: { parallel?: boolean }): Promise<ExecutionReport>\` — Run the workflow.

### Task API

- \`constructor(options: TaskOptions)\`
  - **name**: \`string\` (required, unique)
  - **fn**: \`(context) => Promise<any> | any\` (required, execution body)
  - **retries**: \`number\` (optional, default: 0)
  - **retryDelay**: \`number\` (optional, default: 0ms)
  - **timeout**: \`number\` (optional, timeout in ms)
  - **dependsOn**: \`string[]\` (optional, dependency task names)
  - **condition**: \`(context) => boolean | Promise<boolean>\` (optional)
  - **onFailure**: \`(taskName, error) => void\` (optional callback)
  - **continueOnFailure**: \`boolean\` (optional, default: false)

---

View the package on [npm](https://www.npmjs.com/package/react-pyworkflow) and install in your next frontend project!
        `.trim(),
    },

    /* ─────────────────────────────────────────────────────── */
    /*  Technical: Thoughts on backend API design              */
    /* ─────────────────────────────────────────────────────── */
    {
        slug:        'backend-api-design-principles',
        title:       'Five API Design Principles I Wish I Learned Earlier',
        date:        '2025-01-08',
        tags:        ['backend', 'api', 'python', 'fastapi', 'engineering'],
        excerpt:     'Hard-won rules for designing APIs that stay maintainable as traffic, teams, and requirements scale — drawn from real project experience.',
        readingTime: '6 min read',
        content: `
Designing an API is easy. Designing one that survives contact with real usage is a different skill entirely. Here are five principles I wish someone had handed me earlier.

## 1. Version from day one

Even if v2 never ships, \`/api/v1/\` signals to consumers that you've thought about change. Retrofitting versioning onto an unversioned API is one of the most painful migrations in software.

## 2. Errors are part of the contract

An API that returns \`200 OK\` for everything — including internal server errors — is lying to its consumers. Use status codes correctly. Return structured error bodies with machine-readable codes, not just human-readable messages.

\`\`\`json
{
  "error": {
    "code": "VALIDATION_FAILED",
    "message": "The 'email' field must be a valid email address.",
    "field": "email"
  }
}
\`\`\`

## 3. Idempotency is a feature

Any mutating endpoint that clients will retry (which is all of them, eventually) should be idempotent. Use idempotency keys. Document them. Enforce them.

## 4. Pagination is not optional

Returning an unbounded list of records is a ticking clock. Add \`limit\`/\`offset\` or cursor-based pagination before you have data, not after.

## 5. Log at the boundary, not inside business logic

Log the incoming request and the outgoing response at the API layer. Keep business logic clean. Tracing correlation IDs across services will save hours of debugging.

---

These aren't novel ideas. They're just the ones I learned by breaking them in production and wishing I'd been more deliberate up front.
        `.trim(),
    },

    /* ─────────────────────────────────────────────────────── */
    /*  Arduino GitHub Pusher — Building a One-Click Git       */
    /*  Extension for Arduino IDE 2.x and VS Code              */
    /* ─────────────────────────────────────────────────────── */
    {
        slug:        'arduino-github-pusher',
        title:       'Building a Zero-Dependency Git Client for Arduino IDE 2.x & VS Code',
        date:        '2026-07-15',
        tags:        ['arduino', 'vs-code', 'typescript', 'extensions', 'open-source', 'automation'],
        excerpt:     'How I built a lightweight VS Code / Arduino IDE 2.x extension that lets you commit and push an Arduino sketch to GitHub in a single click — without blocking the UI, without a heavy runtime, and with a single 700 KB bundle.',
        readingTime: '9 min read',
        content: `
## The Problem

Every time I finished a circuit on my Arduino and wanted to back it up to GitHub, I had to leave the editor, open a terminal, type the same four commands, and come back. Multiply that by dozens of iterations across a project and it gets old fast.

The Arduino IDE 2.x is built on the Eclipse Theia framework — a VS Code–compatible editor — which means it supports VS Code extensions. But the marketplace integration isn't always available, and the extension ecosystem for Arduino-specific workflows is thin. I wanted something that would:

- Work in **both VS Code and Arduino IDE 2.x** without modification
- Push the active sketch to GitHub in **one click** — from the status bar, command palette, or toolbar
- Never block the editor thread
- Ship as a single, dependency-free file

## Architecture Decisions

### 1. Async, Non-Blocking File I/O

The biggest mistake most extension authors make is using Node's synchronous \`fs\` APIs (\`readFileSync\`, \`writeFileSync\`) inside an extension host. This blocks the entire VS Code extension host thread and freezes the editor.

I built the entire extension on top of \`vscode.workspace.fs\` — VS Code's asynchronous, URI-based virtual file system API. Every read, write, and directory scan is a \`Promise\` that yields control back to the event loop. The editor stays fully responsive even while staging hundreds of files.

\`\`\`typescript
// Reading a file — never blocks the thread
const bytes = await vscode.workspace.fs.readFile(fileUri)
const content = Buffer.from(bytes).toString('utf8')
\`\`\`

### 2. Zero-Dependency Bundling with esbuild

A common source of extension failures is transitive dependency resolution at runtime. When a user installs a \`.vsix\` manually (which is the only option in Arduino IDE 2.x), missing \`node_modules\` crash the extension on activation.

The solution: bundle everything at build time with **esbuild**. All source files, Octokit REST client, and simple-git are compiled into a single \`out/extension.js\` file (~700 KB). The resulting \`.vsix\` contains no \`node_modules\` directory at all.

\`\`\`bash
esbuild src/extension.ts \\
  --bundle \\
  --platform=node \\
  --external:vscode \\
  --outfile=out/extension.js \\
  --minify
\`\`\`

The \`--external:vscode\` flag tells esbuild to leave the VS Code API import as-is — the host environment always provides it.

### 3. Secure Token Storage with Keychain Fallback

Storing a GitHub Personal Access Token safely is non-trivial. The ideal path is VS Code's \`secrets\` API, which writes to the native OS keychain (Keychain on macOS, Credential Manager on Windows, libsecret on Linux).

But Theia-based environments like Arduino IDE 2.x may not implement the \`secrets\` API. So the extension checks:

1. Try \`context.secrets.store()\` — if available, use it
2. If not, fall back to \`context.globalState\` and store only a hashed representation

This means the extension works securely on VS Code and gracefully degrades in Arduino IDE 2.x without crashing.

### 4. Dual Language ID Detection

VS Code reports \`.ino\` files as language ID \`cpp\`. Arduino IDE 2.x reports them as \`arduino\`. The status bar button and all command guards check for **both**:

\`\`\`typescript
const lang = editor.document.languageId
const isSketch = lang === 'cpp' || lang === 'arduino'
\`\`\`

This single check makes the extension work identically in both editors with no conditional branching or platform detection anywhere else.

## Smart Project Detection

When you open a folder, the extension scans for:

- A \`.ino\` file (primary Arduino sketch file)
- An \`arduino.json\` board configuration
- A \`.git\` directory (already initialized repo)

If it finds sketches but no Git repo, it prompts you to run **Setup GitHub Repository**, which:

1. Initializes Git with \`simple-git\`
2. Generates an opinionated \`.gitignore\` (build output, \`*.elf\`, \`*.hex\`, logs)
3. Creates a \`README.md\` template
4. Calls the Octokit REST API to create the repo on GitHub (public or private)
5. Sets the remote and pushes the initial commit

All async, all non-blocking, all in under 3 seconds on a typical project.

## Installing the Extension

The extension is distributed as a compiled \`.vsix\` file. You can download it directly from this portfolio and install it in either editor.

### VS Code
1. Download \`arduino-github-pusher-1.0.0.vsix\` from the portfolio
2. Open VS Code → Command Palette → **Extensions: Install from VSIX…**
3. Select the downloaded file

### Arduino IDE 2.x (Manual)
1. Locate your plugins directory:
   - **macOS / Linux**: \`~/.arduinoIDE/plugins/\`
   - **Windows**: \`C:\\Users\\<username>\\.arduinoIDE\\plugins\\\`
2. Create a folder: \`arduino-github-pusher/\`
3. Unzip the \`.vsix\` file (it's a standard ZIP) into that folder
4. Restart Arduino IDE 2.x

The folder should look like this:

\`\`\`
~/.arduinoIDE/plugins/arduino-github-pusher/
├── package.json
├── readme.md
└── out/
    └── extension.js
\`\`\`

## Initial Configuration

Once installed, open the Command Palette and run:

> **Arduino GitHub: Configure GitHub Token**

Paste a GitHub Personal Access Token with the \`repo\` scope. The token is stored in the OS keychain — never in any plain-text config file.

Then run **Arduino GitHub: Setup GitHub Repository** the first time to scaffold the project and push the initial commit. After that, every subsequent push is a single click on the **☁ GitHub** button in the status bar.

## What I Learned

Building this extension taught me a few things I'll carry into every tool I build:

- **Block nothing.** The async workspace FS API exists for a reason. Use it unconditionally.
- **Bundle at build time.** Don't trust the user's runtime environment to have your dependencies — ship them yourself.
- **Write for both environments.** Checking for API availability and gracefully degrading is always better than a hard crash.
- **One bundle, two runtimes.** A single compiled \`.vsix\` running in two different editors with zero platform-specific code is a rewarding engineering outcome.

The extension is available for download on this portfolio. Source is kept private — but the compiled binary is free to use.
        `.trim(),
    },
]

/** Look up a single post by slug */
export function getPostBySlug(slug: string): Post | undefined {
    return POSTS.find(p => p.slug === slug)
}

/** Return posts sorted newest → oldest */
export function getSortedPosts(): Post[] {
    return [...POSTS].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    )
}
